import {
  Banknote,
  Bookmark,
  BookmarkCheck,
  Building2,
  ExternalLink,
  Loader2,
  MapPin,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { fmtMoney, labelize } from "@/lib/assessment";

function scoreColour(score) {
  if (score == null) return "text-slate-400";
  if (score >= 70) return "text-emerald-700";
  if (score >= 40) return "text-amber-700";
  return "text-slate-600";
}

function SourceBadge({ source }) {
  return (
    <span
      className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[11px] font-medium text-slate-700"
      data-testid="job-source-badge"
    >
      {labelize(source) || "Unknown"}
    </span>
  );
}

export default function JobCard({
  job,
  onSave,
  onMarkApplied,
  isSaved,
  isApplied,
  savingState,
  applyingState,
}) {
  const salary = (() => {
    const cur = job.salary_currency || "USD";
    const min = fmtMoney(job.salary_min, cur);
    const max = fmtMoney(job.salary_max, cur);
    if (min && max) return `${min} – ${max}`;
    if (min) return `${min}+`;
    if (max) return `Up to ${max}`;
    return null;
  })();

  const cardKey = job.source_job_id || job.job_id;

  return (
    <article
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_6px_22px_rgb(0,0,0,0.04)] hover:shadow-[0_18px_38px_rgb(0,0,0,0.08)] hover:-translate-y-0.5 transition-all"
      data-testid={`job-card-${cardKey}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3
            className="text-base lg:text-lg font-medium text-slate-900"
            data-testid={`job-title-${cardKey}`}
          >
            {job.title}
          </h3>
          <p
            className="mt-0.5 text-sm text-slate-600 truncate flex items-center gap-1.5"
            data-testid={`job-employer-${cardKey}`}
          >
            <Building2 className="h-3.5 w-3.5 text-slate-400" />
            {job.employer || "—"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {job.match_score != null ? (
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs font-medium"
              data-testid={`job-score-${cardKey}`}
            >
              <Sparkles className="h-3 w-3 text-slate-400" />
              <span className={scoreColour(job.match_score)}>
                {job.match_score}/100
              </span>
            </div>
          ) : null}
          <SourceBadge source={job.source} />
        </div>
      </header>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div data-testid={`job-location-${cardKey}`}>
          <dt className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            <MapPin className="h-3 w-3" />
            Location
          </dt>
          <dd className="mt-0.5 text-slate-800">{job.location || "—"}</dd>
        </div>
        <div data-testid={`job-remote-${cardKey}`}>
          <dt className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            <ShieldCheck className="h-3 w-3" />
            Arrangement
          </dt>
          <dd className="mt-0.5 text-slate-800">{labelize(job.remote_type) || "—"}</dd>
        </div>
        <div data-testid={`job-salary-${cardKey}`}>
          <dt className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            <Banknote className="h-3 w-3" />
            Salary
          </dt>
          <dd className="mt-0.5 text-slate-800">{salary || "—"}</dd>
        </div>
        <div data-testid={`job-posted-${cardKey}`}>
          <dt className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            Posted
          </dt>
          <dd className="mt-0.5 text-slate-800">
            {job.posted_date ? new Date(job.posted_date).toLocaleDateString() : "—"}
          </dd>
        </div>
      </dl>

      {Array.isArray(job.why_it_matches) && job.why_it_matches.length > 0 ? (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">
            Why it matches
          </p>
          <ul className="space-y-1" data-testid={`job-why-${cardKey}`}>
            {job.why_it_matches.slice(0, 3).map((r, i) => (
              <li
                key={`${i}-${String(r).slice(0, 32)}`}
                className="flex items-start gap-1.5 text-xs text-slate-600"
              >
                <span className="mt-1.5 inline-block h-1 w-1 flex-none rounded-full bg-slate-400" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onSave(job)}
          disabled={savingState === "loading"}
          className={
            "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-all disabled:opacity-60 " +
            (isSaved
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
          }
          data-testid={`job-save-${cardKey}`}
        >
          {isSaved ? (
            <BookmarkCheck className="h-3.5 w-3.5" />
          ) : (
            <Bookmark className="h-3.5 w-3.5" />
          )}
          {isSaved ? "Saved" : "Save job"}
        </button>

        <button
          type="button"
          onClick={() => onMarkApplied(job)}
          disabled={applyingState === "loading" || isApplied}
          className={
            "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-all disabled:opacity-60 " +
            (isApplied
              ? "border-sky-200 bg-sky-50 text-sky-800"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
          }
          data-testid={`job-mark-applied-${cardKey}`}
        >
          <Send className="h-3.5 w-3.5" />
          {isApplied ? "Marked applied" : "Mark as applied"}
        </button>

        {job.apply_url ? (
          <a
            href={job.apply_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-slate-800 transition-all"
            data-testid={`job-apply-${cardKey}`}
          >
            Apply
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </div>
    </article>
  );
}
